/** Void & delete a posted invoice / delete a whole split — reason goes on the ledger audit trail. */
import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from '../ui/alert-dialog';

function ReasonDialog({ open, onOpenChange, title, description, reasonRequired, confirmLabel, onConfirm, busy, testid }) {
  const [reason, setReason] = useState('');
  const ok = !reasonRequired || reason.trim().length >= 5;
  return (
    <AlertDialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setReason(''); }}>
      <AlertDialogContent className="bg-[#1E2530] border-white/10 text-white" data-testid={testid}>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-400" /> {title}</AlertDialogTitle>
          <AlertDialogDescription className="text-gray-400">{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder={reasonRequired ? 'Reason (required, at least 5 characters) — e.g. test entry posted in error' : 'Reason (optional)'} className="bg-[#2A3240] border-white/10 text-white" data-testid={`${testid}-reason`} />
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid={`${testid}-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); onConfirm(reason.trim()); }} disabled={busy || !ok} className="bg-red-500 text-white hover:bg-red-400 disabled:opacity-50" data-testid={`${testid}-confirm`}>
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />} {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function VoidDeleteButton({ api, item, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!item || item.status !== 'posted') return null;
  const run = async (reason) => {
    setBusy(true);
    try {
      const r = await api.post(`/cost-inbox/${item.id}/void-delete`, { reason });
      toast.success(`Voided ${r.data.voided_rows} ledger row${r.data.voided_rows === 1 ? '' : 's'} and moved the invoice to Deleted`, { description: r.data.note || undefined, duration: r.data.note ? 9000 : 4000 });
      setOpen(false); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not void the invoice'); }
    finally { setBusy(false); }
  };
  const sent = item.xero_send?.status === 'sent';
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="border-red-500/40 text-red-300 bg-red-500/10 hover:bg-red-500/20" data-testid="inbox-void-delete-btn">
        <Trash2 className="w-4 h-4 mr-1.5" /> Void & delete
      </Button>
      <ReasonDialog open={open} onOpenChange={setOpen} busy={busy} reasonRequired confirmLabel="Void & delete" onConfirm={run} testid="inbox-void-delete-dialog"
        title="Void this posted invoice?"
        description={`Every ledger row posted from it is voided (kept on the audit trail, excluded from job totals) and the invoice moves to Deleted. The Sends history stays.${sent ? ' It was already emailed to Xero — delete the draft bill there as well.' : ''}`} />
    </>
  );
}

export function DeleteSplitButton({ api, item, kids, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!item || item.status !== 'split') return null;
  const anyPosted = (kids || []).some(k => k.status === 'posted');
  const run = async (reason) => {
    setBusy(true);
    try {
      const r = await api.post(`/cost-inbox/${item.id}/delete-split`, { reason });
      toast.success(`Deleted the split and ${r.data.copies_deleted} cop${r.data.copies_deleted === 1 ? 'y' : 'ies'}${r.data.voided_rows ? ` · voided ${r.data.voided_rows} ledger row${r.data.voided_rows === 1 ? '' : 's'}` : ''}`, { description: r.data.note || undefined, duration: r.data.note ? 9000 : 4000 });
      setOpen(false); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete the split'); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1 underline text-red-300 hover:text-red-200" data-testid="inbox-delete-split-btn"><Trash2 className="w-3.5 h-3.5" /> Delete split</button>
      <ReasonDialog open={open} onOpenChange={setOpen} busy={busy} reasonRequired={anyPosted} confirmLabel="Delete split" onConfirm={run} testid="inbox-delete-split-dialog"
        title="Delete this split and both copies?"
        description={anyPosted ? 'A copy has been posted: its ledger rows are voided first (reason required), then the original and both copies move to Deleted. Any bill already emailed to Xero must be removed there by hand.' : 'The original and both copies move to Deleted (restorable for 30 days).'} />
    </>
  );
}
